import * as THREE from 'three';
import type { Input } from '../core/Input';
import type { AudioManager } from '../audio/AudioManager';
import type { PlayerController } from '../player/PlayerController';
import type { Brush } from '../physics/Brush';

const REACH = 1.35;
const ON_TOP = 1.05;
const CLIMB_REACH = 0.82;
const LOOK_UP = 0.38;
const OPEN_SPEED = 3.4;
const OPEN_ANGLE = Math.PI * 0.52;

interface GrateRuntime {
  root: THREE.Object3D;
  hinge: THREE.Object3D;
  brush: Brush | null;
  openT: number;
}

/** Floor grate in the start vents — F or shoot swings it down into the stall. */
export class VentGrateSystem {
  private readonly grates: GrateRuntime[] = [];
  private readonly to = new THREE.Vector3();
  private readonly projected = new THREE.Vector3();

  constructor(
    scene: THREE.Object3D,
    private readonly audio: AudioManager,
  ) {
    scene.traverse((o) => {
      if (o.name !== 'vent_grate') return;
      const hinge = o.getObjectByName('vent_grate_hinge') ?? o;
      this.grates.push({
        root: o,
        hinge,
        brush: (o.userData.brush as Brush | undefined) ?? null,
        openT: 0,
      });
    });
  }

  update(player: PlayerController, input: Input, dt: number, allowInteract = true): boolean {
    this.tick(dt);
    if (!player.isClimbing && this.wantClimb(player, input)) {
      const climb = this.pickClimb(player);
      if (climb) this.climb(player, climb);
    }
    if (!allowInteract || !input.interactPressed) return false;
    const g = this.pickOpen(player);
    if (!g) return false;
    this.open(g);
    return true;
  }

  lookTip(
    player: PlayerController,
    camera: THREE.Camera,
    width: number,
    height: number,
  ): { label: string; x: number; y: number } | null {
    const g = this.pickOpen(player);
    if (!g || width < 8 || height < 8) return null;
    g.root.getWorldPosition(this.projected);
    this.projected.project(camera);
    if (this.projected.z < -1 || this.projected.z > 1) return null;
    return {
      label: 'F - Open grate',
      x: Math.min(width - 40, Math.max(40, (this.projected.x * 0.5 + 0.5) * width)),
      y: Math.min(height - 24, Math.max(22, (-this.projected.y * 0.5 + 0.5) * height)),
    };
  }

  anyMotion(): boolean {
    for (const g of this.grates) {
      if (g.openT > 0 && g.openT < 1) return true;
    }
    return false;
  }

  anyOpen(): boolean {
    for (const g of this.grates) {
      if (g.root.userData.destroyed || g.openT > 0.05) return true;
    }
    return false;
  }

  breakFromShot(obj: THREE.Object3D): boolean {
    const g = this.fromHit(obj);
    if (!g || g.root.userData.destroyed) return false;
    this.open(g);
    return true;
  }

  private open(g: GrateRuntime): void {
    if (g.root.userData.destroyed) return;
    g.root.userData.destroyed = true;
    g.root.userData.ignoreHit = true;
    if (g.brush) g.brush.noCollision = true;
    this.audio.play('openVent', { volume: 0.85, rate: 0.96 + Math.random() * 0.08 });
  }

  private tick(dt: number): void {
    for (const g of this.grates) {
      if (!g.root.userData.destroyed || g.openT >= 1) continue;
      g.openT = Math.min(1, g.openT + dt * OPEN_SPEED);
      const u = g.openT * g.openT;
      g.hinge.rotation.x = u * OPEN_ANGLE;
    }
  }

  private fromHit(obj: THREE.Object3D): GrateRuntime | null {
    let o: THREE.Object3D | null = obj;
    while (o) {
      const hit = this.grates.find((g) => g.root === o);
      if (hit) return hit;
      o = o.parent;
    }
    return null;
  }

  private climb(player: PlayerController, g: GrateRuntime): void {
    let dest = g.root.userData.climbDest as THREE.Vector3 | undefined;
    if (!dest) {
      g.root.getWorldPosition(this.to);
      dest = this.to.set(this.to.x, this.to.y + 0.12, this.to.z + 0.55);
    }
    player.startClimb(dest, { crouch: true, duration: 0.45 });
    this.audio.play('playerGrunt', { volume: 0.42, rate: 0.92 + Math.random() * 0.08 });
  }

  private pickOpen(player: PlayerController): GrateRuntime | null {
    const pos = player.body.position;
    let best: GrateRuntime | null = null;
    let bestD = Infinity;
    for (const g of this.grates) {
      if (g.root.userData.destroyed) continue;
      g.root.getWorldPosition(this.to);
      const dx = this.to.x - pos.x;
      const dz = this.to.z - pos.z;
      const d = Math.hypot(dx, dz);
      const onTop = pos.y > this.to.y - 0.35 && d < ON_TOP;
      if (!onTop && d > REACH) continue;
      if (d < bestD) {
        bestD = d;
        best = g;
      }
    }
    return best;
  }

  private wantClimb(player: PlayerController, input: Input): boolean {
    if (player.pitch < LOOK_UP) return false;
    return input.jumpPressed || (!player.body.grounded && player.body.velocity.y > 0.4);
  }

  private pickClimb(player: PlayerController): GrateRuntime | null {
    const pos = player.body.position;
    for (const g of this.grates) {
      if (!g.root.userData.destroyed || g.openT < 0.65) continue;
      g.root.getWorldPosition(this.to);
      if (pos.y > this.to.y - 0.7) continue;
      const d = Math.hypot(this.to.x - pos.x, this.to.z - pos.z);
      if (d <= CLIMB_REACH) return g;
    }
    return null;
  }
}
